import { Expose, Type, Exclude } from 'class-transformer';
import { Point } from 'geojson';

import { IDevice } from '@lib/axon/interfaces/device.interface';
import { AxonDeviceModels } from '@lib/axon/models/device-model.enum';
import { ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';

import { SnOrganization } from '../../organization';

export class FsAxonDeviceAttributes extends SnModel {
  @Expose()
  public assignees: any[];

  @Expose()
  public batteries: any[];

  @Expose()
  public deviceSerial: string;

  @Expose()
  public location: any;

  @Expose()
  public signalStrengths: any[];

  @Expose()
  public rawStatus: string;

  @Expose()
  public status: string;

  @Expose()
  public stealth: {
    state: string;
  };

  @Expose()
  public stream: any;

  @Expose()
  public axonDeviceId: string;

  @Expose()
  public deviceModel: string;

  @Expose()
  public deviceUpdateTimestamp: number;

  @Expose()
  public partnerId: string;

  @Expose()
  public partnerName: string;

  @Expose()
  public correlation: {
    id: string;
    timestamp: number;
  };

  @Expose()
  public vehicle: any;

  get primaryAssignee(): any {
    if (!this.assignees?.length) {
      return null;
    }
    return this.assignees[0];
  }

  @Expose()
  public watchMe: any;
}

export class FsAxonConnectedDevice {
  @Expose()
  public axonDeviceId: string;
}

export class FsAxonDevice extends SnModel implements IDevice {
  @Expose()
  public id: string;

  @Expose()
  public name: string;

  @Expose()
  public address: string;

  @Expose()
  public city: string;

  @Expose()
  public state: string;

  @Expose()
  public zip: string;

  @Expose()
  public media: any;

  @Expose()
  public type: any;

  @Expose()
  public device: {
    displayName: string;
    category: string;
    originator: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  };

  @ExposeRawData()
  public attributes: FsAxonDeviceAttributes;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnOrganization)
  public organization: SnOrganization;

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @Expose()
  public createdTimestamp: Date;

  @Expose()
  public axonDeviceId: string;

  @Expose()
  public deviceModel: string;

  @Expose()
  public deviceModelInternal: string;

  @Expose()
  public deviceUpdateTimestamp: number;

  @Expose()
  public partnerId: string;

  @Expose()
  public partnerName: string;

  @Expose()
  public connectedDevices: FsAxonConnectedDevice[];

  get deviceName(): string {
    const { deviceModel, deviceModelInternal, attributes, name } = this;
    switch (deviceModel || deviceModelInternal) {
      case AxonDeviceModels.AxonBody3:
      case AxonDeviceModels.AxonBody4:
      case AxonDeviceModels.AxonBodyWorkforce:
      case AxonDeviceModels.AxonBodyWorkforceMini:
        if (attributes?.assignees) {
          return `${attributes?.assignees[0]?.firstName}
            ${attributes?.assignees[0]?.lastName}`;
        }
        return 'Unassigned';
      case AxonDeviceModels.AxonFleet3Hub:
        if (attributes?.vehicle?.vehicleName.length > 0) {
          return `${attributes?.vehicle?.vehicleName}`;
        }
        return 'Unassigned';
      case AxonDeviceModels.AxonOutpost:
      case AxonDeviceModels.AxonLightpost:
        // For fixed cameras, use device name or location
        return name || 'Unassigned';
      default:
        return 'Unassigned';
    }
  }

  get filteredAnaltyicData(): any {
    const { axonDeviceId, deviceModel, deviceUpdateTimestamp, partnerId, attributes } = this;
    const {
      assignees = [],
      batteries = [],
      signalStrengths = [],
      stream = {},
      deviceSerial,
      rawStatus,
      status,
      stealth,
      correlation,
    } = attributes || {};

    const { watchMe = {} } = stream;

    return {
      device: {
        deviceId: axonDeviceId,
        deviceModel,
        deviceUpdateTimestamp,
        partnerId,
        userId: assignees[0]?.userId,
        batteryLevel: batteries[0]?.batteryPercentage,
        serial: deviceSerial,
        rawStatus,
        signalStrength: {
          strength: signalStrengths[0]?.signalStrength,
          networkType: signalStrengths[0]?.networkType,
        },
        status,
        stealth: stealth?.state,
        isStreamable: stream?.isStreamable,
        isStreamableReason: stream?.isStreamableReason,
        watchMe: {
          isActive: watchMe?.isWatchMeActive,
          activationTime: watchMe?.watchMeActivationTime,
        },
        correlationId: correlation?.id,
      },
    };
  }

  constructor(data: any = {}) {
    super();

    this.fromJSON(data);
  }
  deviceStatus: string;
  lastUpdated: string;
}
